import { Injectable, inject } from '@angular/core';
import { ConfigService, LoggedInUserService, OfflineStatusService, select } from '@formbird/services';
import { MapViewsToCache, OfflineStatus } from "@formbird/types";
import { Observable } from 'rxjs';
import { CheckConnectionService } from './check-connection.service';


const MIN_ZOOM = 1;
const MAX_ZOOM = 20;

const logger = console;

@Injectable({
  providedIn: 'root'
})
export class CacheMapTilesService {
  private configService = inject(ConfigService);
  private offlineStatusService = inject(OfflineStatusService);
  private loggedInUserService = inject(LoggedInUserService);
  private checkConnectionService = inject(CheckConnectionService);

  @select(['offlineReducer', 'offlineStatus']) offlineStatus$: Observable<OfflineStatus>;
  
  shouldRetryFetch: boolean = false;
  strUserConfigItem = "lastSyncTileServerCacheCount";
  mapViewsToCache: MapViewsToCache[] = [];
  cacheName: string = "map-tiles";
  cacheStorage: any;
  isOnline: boolean = true;
  checkConnectionSubscription: any;

  constructor() {

    const clConfig = this.configService.clientConfig();
    if (clConfig && clConfig.openLayers && clConfig.openLayers.mapViewsToCache){
      this.mapViewsToCache = clConfig.openLayers.mapViewsToCache;
    }

    const accountDocumentId = localStorage.getItem('LoggedInUserID');
    // console.log("====> cache-map-tiles, accountDocumentId = ", accountDocumentId);
    this.cacheName = `tiles-${accountDocumentId}`;

    // window.addEventListener('online', () => this.handleNetworkChange());
    // window.addEventListener('offline', () => this.handleNetworkChange());

    // this.offlineStatusService.offlineStatus$.subscribe((offlineStatusData: any) => {
    //   if (this.isOnline !== offlineStatusData?.connected) {
    //     this.isOnline = offlineStatusData.connected;
    //     this.handleNetworkChange();
    //   }
    // });

    // this.offlineStatus$.subscribe((offlineStatusData) => {
    //   if (this.isOnline !== offlineStatusData?.connected) {
    //     this.isOnline = offlineStatusData.connected;
    //     this.handleNetworkChange();
    //   }
    // });
  }

  subscribeToConnection() {
    if (!this.checkConnectionSubscription) {
      this.checkConnectionService.startCheckingConnection();

      this.checkConnectionSubscription = this.checkConnectionService.connectionStatus$
      .subscribe(status => {
        if (this.isOnline !== status) {
          this.isOnline = status;
          this.handleNetworkChange();
        }
        logger.info('Subscribed - Connection status:', status ? 'Online' : 'Offline');
      });
      logger.info('Subscribed to connection status updates.');
    } else {
      logger.info('Already subscribed.');
    }
  }
  unsubscribeFromConnection() {
    if (this.checkConnectionSubscription) {
      this.checkConnectionSubscription.unsubscribe();
      this.checkConnectionSubscription = null;

      this.checkConnectionService.stopCheckingConnnection();
      logger.info('Unsubscribed from connection status updates.');
    } else {
      logger.info('Not currently subscribed.');
    }
  }

  private longToTile(lon, zoom) {
    let result;

    if (!lon || !zoom) {
      const msg = "No longitude or zoom specified when converting longitude to tile";
      logger.error(msg);
      throw new Error(msg);
    } else {
      result = (Math.floor((lon + 180) / 360 * Math.pow(2, zoom)));
    }

    return result;
  }

  private latToTile(lat, zoom) {
    let result;

    if (!lat || !zoom) {
      const msg = "No latitude or zoom specified when converting latitude to tile";
      logger.error(msg);
      throw new Error(msg);
    } else {
      result = (Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 *
        Math.pow(2, zoom)));
    }

    return result;
  }


  private calculateTilesWithinRegion(point1, point2, tileServerUrl, minZoom, maxZoom) {
    let arrayCoords = [];

    for (let zoom = minZoom; zoom <= maxZoom; zoom++) {
      var point1X = this.longToTile(point1.lon, zoom);
      var point2X = this.longToTile(point2.lon, zoom);
      var point1Y = this.latToTile(point1.lat, zoom);
      var point2Y = this.latToTile(point2.lat, zoom);

      var startX = Math.min(point1X, point2X);
      var endX = Math.max(point1X, point2X);
      var startY = Math.min(point1Y, point2Y);
      var endY = Math.max(point1Y, point2Y);

      for (let x = startX; x <= endX; x++) {
        for (let y = startY; y <= endY; y++) {
          var url = tileServerUrl;
          url = url.replace("{z}", zoom);
          url = url.replace("{x}", x);
          url = url.replace("{y}", y);
          arrayCoords.push({url, x, y});
        }
      }
    }

    return arrayCoords;
  }

  private loadTileServerVectorStyle(tileServerUrl) {

    let url = tileServerUrl;
    url = url.replace("data/v3/{z}/{x}/{y}.pbf", 'styles/basic-preview/style.json');
    return fetch(url);

  }

  async loadDataCache(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        this.subscribeToConnection();

        this.offlineStatusService.setMapTilesCachingCompleted(false);

        await this.loadTileServerGL();

      } catch (err) {
        this.unsubscribeFromConnection();

        const errMsg = 'Unable to cache tile serverGL.' + err;
        logger.error(errMsg);
        reject(new Error(errMsg));
        return;
      }

      this.offlineStatusService.setMapTilesCachingCompleted(true);
      this.offlineStatusService.checkCachingCompleted();
      logger.info('Done caching map tiles.');
      
      this.unsubscribeFromConnection();

      resolve();
    });

  }

  private async waitUntilConnectionRestored() {
    return new Promise((resolve) => {
      const interval = setInterval(() => {
        if (this.isOnline) {
          clearInterval(interval);
          resolve(true);
        }
      }, 1000); // check every 1000 seconds
    });
  }

  private async fetchWithRetry(url, retries = 3, backoff = 1000) {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`fetch with retry ${retries}, failed to fetch: ${url}`);
      }
      return response;
    } catch (error) {
      if (this.isOnline === false) {
        logger.info(`Offline when fetching url: ${url}, wait until connection restore...`);
        await this.waitUntilConnectionRestored();
        return this.fetchWithRetry(url, retries, backoff); // no reduction in retries
      } 
      
      // If retries > 0 and not related to connection error, continue retrying with backoff
      if (retries > 0) {
        logger.info(`Retrying...(${retries} retries left) for url: ${url}`);
        await new Promise((resolve) => setTimeout(resolve, backoff)); // wait before retrying
        return this.fetchWithRetry(url, retries - 1, backoff * 2); // increase back off
      } else {
        logger.info(`Retrying url ${url} has another error:`);
        throw error; // end of retries and has another error
      }
    }
  }
  updateOfflineStatus() {
    this.offlineStatusService.offlineStatus.tileServerCacheCount++;
    this.offlineStatusService.setOfflineStatusToLocalStorage();
    this.loggedInUserService.setUserConfigItem(this.strUserConfigItem, this.offlineStatusService.offlineStatus.tileServerCacheCount);
  }

  private async loadTile(coordArray, index) {
    if (this.isOnline === false) {
      logger.info(`Network is offline, waiting to resume... at: ${index}/${coordArray.length}
        ,\n\t url = ${coordArray[index].url}`
      );
      return new Promise(resolve => {
        const checkOnline = setInterval(() => {
          // logger.info(`***** interval check online of index: ${index}
          //   , isOnline = ${this.isOnline}
          //   , coord array.len = ${coordArray.length}
          //   , url = ${coordArray[index].url}`);
          if (this.isOnline === true) {
            clearInterval(checkOnline);
            resolve(this.loadTile(coordArray, index)); // Resume the function when online
          }
        }, 1000); // Check every second
      });
    }

    logger.debug('***** loadEach : ' + index + '/' + coordArray.length);

    if (!this.offlineStatusService.offlineStatus.cacheLoading) {
      const errMsg = "Caching TileServerGL terminated due to cache loading cancelled";
      return Promise.reject(new Error(errMsg))
    }

    return new Promise(async (resolve, reject) => {
      if (coordArray[index]) {
        try {
          const response = await this.fetchWithRetry(coordArray[index].url); // fetch(coordArray[index].url);

          if (response.ok) {
            if (response.status !== 200) {
              logger.warn(`Response status ${response.status} for url ${coordArray[index].url}`);
            }
            await this.cacheStorage.add(coordArray[index].url);

            resolve(null);
          } else {
            const msg = `Failed to fetch tile: ${coordArray[index].url}`;
            logger.info(msg);
            reject({message: msg});
          }
          
        } catch (err) {
          logger.info(`Exception: Fetch url ${coordArray[index].url} error:`, err);
          reject(err);
        } finally {
          this.updateOfflineStatus();
        }
      } else {
        this.updateOfflineStatus();
        resolve(null);
      }
    });
  }

  private generateCoordinates(cacheBounds, mapView) {
    let point1 = { lat: cacheBounds[1], lon: cacheBounds[0] };
    let point2 = { lat: cacheBounds[3], lon: cacheBounds[2] };
    const minZoom = (typeof mapView.minZoom === 'number') ? mapView.minZoom : MIN_ZOOM;
    const maxZoom = (typeof mapView.maxZoom === 'number') ? mapView.maxZoom : MAX_ZOOM;
    logger.info('**** generateCoordinates, url: ' + mapView.url + ', minZoom = ' + minZoom
      + ', maxZoom = ' + maxZoom
    );

    return this.calculateTilesWithinRegion(point1, point2
      , mapView.url, minZoom, maxZoom);
  }

  private async calculateStartIndex(coordArray): Promise<number> {
    let index = 0;
    for (; index < coordArray.length; index++) {
      const { url } = coordArray[index];
      const response = await caches.match(url);
      if (!response) {
        break;
      }
    }
    return index;
  }

  private async processDataTiles(coordArray): Promise<any> {
    // logger.info('**** processDataTiles, coordArray = ', coordArray);
    // logger.info('**** processDataTiles, coordArray.len = ' + coordArray.length);


    const startIndex = await this.calculateStartIndex(coordArray);
    this.offlineStatusService.offlineStatus.tileServerCacheCount = startIndex - 1;
    console.log(`**** start index = ${startIndex}`);


    this.offlineStatusService.offlineStatus.tileServerMaxCacheCount = coordArray.length;
    

    // return new Promise((resolve) => { resolve(true); }); // test test
    return this.processRange(startIndex, coordArray.length, index => this.loadTile.call(this, coordArray, index))
      .then(result => {
        this.offlineStatusService.offlineStatus.tileServerCacheCount = 0;
        return result;
      })
      .catch(err => {
        this.offlineStatusService.offlineStatus.tileServerCacheCount = 0;
        throw err;
      });
  }

  private handleNetworkChange() {
    if (this.isOnline === true && this.shouldRetryFetch === true) {
      logger.info('Network is back online, resuming fetch...');
      this.offlineStatusService.setCacheLoadingStatus(true);
      this.loadTileServerGL(); // Retry fetching
    }
  }
  async getBoundsFromUrl(url: string): Promise<[number, number, number, number]> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(url);
        const data = await response.json();
        resolve(data.bounds);
      } catch (error) {
        logger.info('*** Error getBoundsFromUrl ' + url);
        logger.info(error);
        resolve(null);
      }
    });
    
  }

  private loadStyle(url) {
    return new Promise(async (resolve, reject) => {
      try {
        logger.info('**** fetch style url = ' + url);
        const response = await fetch(url);
        if (response.ok) {
          await this.cacheStorage.add(url);
        } else {
          logger.error('Fetch failed for URL: ' + url);
        }
        
      } catch (err) {
        const errMsg = 'Unable to cache tile serverGL vector style.' + err;
        logger.error(errMsg);
      }
      resolve(true);
    });
  }

  private async loadStyles(mapView) {
    const promises = [];
    if (Array.isArray(mapView.styleLocationUrls)) {
      mapView.styleLocationUrls.forEach(async (url) => {
        promises.push(this.loadStyle(url));
      });
    } else {
      logger.error('styleLocationUrls is not an array or is undefined:', mapView.styleLocationUrls);
    }

    return await Promise.all(promises);
  }
  private isValidUrl(urlString: string): boolean {
    let url;
    try {
      url = new URL(urlString);
    } catch (_) {
      return false;  
    }

    return url.protocol === "http:" || url.protocol === "https:";
  }

  private loadTileServerGL() {
    if (this.isOnline === false) {
      logger.info('Network is offline, waiting to resume...');
      this.shouldRetryFetch = true;
      return Promise.resolve(null); // Exit if there's no network
    }

    return new Promise(async (resolve, reject) => {
      this.cacheStorage = await caches.open(this.cacheName);
      logger.info('**** mapViewsToCache = ', this.mapViewsToCache);


      // load map tiles if there has dataBounds
      let coordArray = [];
      let urlSet = new Set();
      
      const dataBoundsPromises = this.mapViewsToCache.map(async (mapView) => {
        if (mapView.dataBounds?.length > 0) {
          for (const bound of mapView.dataBounds) {
            if ('topLeftCoord' in bound && 'bottomRightCoord' in bound) {
              const [left, top] = bound.topLeftCoord;
              const [right, bottom] = bound.bottomRightCoord;
              const cacheBounds = [left, top, right, bottom];
              if ( Array.isArray(cacheBounds) && cacheBounds.length === 4
                 && cacheBounds.some(value => typeof value === 'number')) {

                logger.info('Loading tileserver-gl data cache from topLeftCoord & bottomRightCoord.'
                  + '\n\t Url: ' + mapView.url
                  + '\n\t With bounds: ', cacheBounds);
    
                const coords = this.generateCoordinates(cacheBounds, mapView);
                // check duplicate url
                coords.forEach(item => {
                  if (!urlSet.has(item.url)) {
                    urlSet.add(item.url);
                    coordArray.push(item);
                  }
                });
                // logger.info('**** \t\t bound array, coordArray.len = ' + coordArray.length
                //   + ', coords = ', coords
                // );
              }
              
            }
        
            if ('geohash' in bound) {
              // TO DO : convert geohash to lat/lng
              // allBounds.push(convertGeohashToBounds(bound.geohash));
            }
        
            if ('urlConfigBound' in bound) {
              if (this.isValidUrl(bound.urlConfigBound)) {
                const urlBounds = await this.getBoundsFromUrl(bound.urlConfigBound);
                if ( Array.isArray(urlBounds) && urlBounds.length === 4
                 && urlBounds.some(value => typeof value === 'number')) {
                  logger.info('Loading tileserver-gl data cache from urlConfigBound.'
                    + '\n\t Url: ' + mapView.url
                    + '\n\t With bounds: ', urlBounds);
  
                  const coords = this.generateCoordinates(urlBounds, mapView);
                  // check duplicate url
                  coords.forEach(item => {
                    if (!urlSet.has(item.url)) {
                      urlSet.add(item.url);
                      coordArray.push(item);
                    }
                  });
                  
                  // logger.info('**** \t\t urlConfigBound, coordArray.len = ' + coordArray.length
                  //   + ', coords = ', coords
                  // );
                }
              }
            }
          }
        }
      });
      // Wait for all dataBoundsPromises to complete
      await Promise.all(dataBoundsPromises);
      
      if ( coordArray.length == 0 ) {
        logger.info('No tile server is configured');
        return resolve(true);
      }

      
      
      const results = await Promise.all([
        this.mapViewsToCache.map((mapView) => this.loadStyles(mapView))
        , this.processDataTiles( coordArray)
      ]);
    
      resolve(results);
    });
  }

  private processRange(startIndex, endIndex, asyncJob?): Promise<any> {
    let sequence = Promise.resolve();

    for (let i = startIndex; i <= endIndex; i++) {
      sequence = sequence.then(() => {
        if (this.loggedInUserService.getUserConfigItem('isRunningCacheClear')
          || !this.offlineStatusService.isCachingEnabled()) {
          return Promise.resolve('STOP');
        }

        return asyncJob(i);
      })
        .catch(err => {
          logger.error(err.message);
          return asyncJob(i);
        })
        .then((result) => {
          if (result === 'STOP') {
            return Promise.reject('Process stopped');
          }
          return result;
        });
    }

    return sequence.catch((error) => {
      if (error === 'Process stopped') {
        return Promise.resolve('Process was stopped early');
      }
      return Promise.reject(error);
    });
  }
}
